import { useEffect, useRef, useState } from 'react';
import type { CollaborationTransport, SharedSnapshot } from '../../collaboration/protocol';
import { collaborationError, savedDisplayName, saveDisplayName } from '../../collaboration/projectLinks';
import { Icon } from '../Icon';
import { t } from '../../i18n/I18n';

export function JoinSharedProject({ documentId, invite, configured, getTransport, onJoined, onClose }: {
  documentId: string; invite: string; configured: boolean; getTransport: () => CollaborationTransport;
  onJoined: (snapshot: SharedSnapshot) => Promise<void>; onClose: () => void;
}) {
  const [name, setName] = useState(savedDisplayName), [title, setTitle] = useState(() => t('#CollabSharedProject'));
  const [loading, setLoading] = useState(true), [joining, setJoining] = useState(false), [error, setError] = useState('');
  const [attempt, setAttempt] = useState(0);
  const controller = useRef<AbortController | null>(null);
  const active = useRef(true);
  useEffect(() => {
    active.current = true;
    return () => { active.current = false; controller.current?.abort(); };
  }, []);
  useEffect(() => {
    const abort = new AbortController(); controller.current = abort;
    setLoading(true); setError('');
    if (!configured) { setLoading(false); return () => abort.abort(); }
    void getTransport().inspectInvite(invite, abort.signal).then(value => {
      if (abort.signal.aborted) return;
      if (value.documentId !== documentId) throw Object.assign(new Error('Invalid invitation'), { code: 'invalid' });
      setTitle(value.name); setLoading(false);
    }).catch(failure => { if (!abort.signal.aborted) { setError(collaborationError(failure)); setLoading(false); } });
    return () => abort.abort();
  }, [documentId, invite, configured, getTransport, attempt]);
  const join = async () => {
    if (joining || !name.trim()) return;
    setJoining(true); setError('');
    const abort = new AbortController(); controller.current = abort;
    try {
      saveDisplayName(name);
      const snapshot = await getTransport().join(invite, name.trim(), abort.signal);
      if (!active.current || abort.signal.aborted) return;
      if (snapshot.documentId !== documentId) throw Object.assign(new Error('Invalid invitation'), { code: 'invalid' });
      await onJoined(snapshot);
    } catch (failure) { if (active.current && !abort.signal.aborted) setError(collaborationError(failure)); }
    finally { if (active.current) setJoining(false); }
  };
  return <main className="ws-project-loading collab-join">
    <button type="button" className="ws-button" onClick={onClose}><Icon name="back" size={18} />{t('#CollabBackToProjects')}</button>
    <section><span className="ws-loading-mark"><Icon name="link" size={38} /></span><span className="ws-eyebrow">{t('#CollabEyebrow')}</span><h1>{title}</h1>
      {!configured ? <p role="status">{t('#CollabUnavailable')}</p>
        : loading ? <><span className="ws-loading-progress" aria-hidden="true"><span /></span><p role="status">{t('#CollabCheckingInvite')}</p></>
          : <form onSubmit={event => { event.preventDefault(); void join(); }}>
            <p>{t('#CollabJoinLead')}</p>
            <label className="ws-field"><span>{t('#CollabYourName')}</span><input autoFocus required maxLength={48} autoComplete="nickname" value={name} disabled={joining} onChange={event => setName(event.target.value)} placeholder={t('#CollabNamePlaceholder')} /></label>
            <p className="ws-caption">{t('#CollabGuestNote')}</p>
            <button className="ws-button primary collab-wide" disabled={joining || !name.trim() || !!error} type="submit">{joining ? t('#CollabJoining') : t('Join')}</button>
            {joining && <p role="status">{t('#CollabOpeningShared')}</p>}
          </form>}
      {error && <><p className="ws-error" role="alert">{error}</p><button type="button" className="ws-button" disabled={joining} onClick={() => setAttempt(value => value + 1)}>{t('#CollabRetryCheck')}</button></>}
    </section>
  </main>;
}
